import {
  InputTypes,
  PaymentFormField,
  PaymentTypes,
  ValidationStatuses,
  ValidatorTypes,
} from '@cashier/cashier-model';

import { AmountFieldValidator } from './amountFieldValidator';
import { CommonValidatorMap } from './commonValidatorMap';
import { convertRegexFromString } from './convertRegexFromString';

interface ValidationReport {
  status: ValidationStatuses;
  errors: string[];
}

export const FieldValidator = {
  [ValidatorTypes.AMOUNT]: (
    field: PaymentFormField,
    context: {
      paymentType: PaymentTypes;
      balance: string | null;
    },
  ): ValidationReport => {
    const { value } = field;

    if (value === null) {
      return {
        status: field.required
          ? ValidationStatuses.UNDEFINED
          : ValidationStatuses.SUCCESS,
        errors: [],
      };
    }

    if (field.required && value === '') {
      return {
        status: ValidationStatuses.FAILED,
        errors: [],
      };
    }

    const { min, max, scale } = field.params || {};
    const { paymentType, balance } = context;

    const preparedValue = (value as string).replace(',', '.');

    const characterTypeReport =
      AmountFieldValidator.byCharacterType(preparedValue);
    if (!characterTypeReport.isValid) {
      return {
        status: ValidationStatuses.FAILED,
        errors: characterTypeReport.errorMessage
          ? [characterTypeReport.errorMessage]
          : [],
      };
    }

    const errors: string[] = [];

    if (paymentType === PaymentTypes.Withdrawal && balance !== null) {
      const insufficientFundsReport = AmountFieldValidator.byBalance(
        preparedValue,
        balance,
      );

      if (
        !insufficientFundsReport.isValid &&
        insufficientFundsReport.errorMessage
      ) {
        errors.push(insufficientFundsReport.errorMessage);
      }
    }

    const limitReport = AmountFieldValidator.byLimit(
      preparedValue,
      { min, max },
      { paymentType },
    );

    if (!limitReport.isValid && limitReport.errorMessage) {
      errors.push(limitReport.errorMessage);
    }

    if (scale !== undefined) {
      const scaleReport = AmountFieldValidator.byScale(preparedValue, scale);
      if (!scaleReport.isValid && scaleReport.errorMessage) {
        errors.push(scaleReport.errorMessage);
      }
    }

    return {
      status:
        errors.length === 0
          ? ValidationStatuses.SUCCESS
          : ValidationStatuses.FAILED,
      errors: errors,
    };
  },
  [ValidatorTypes.COMMON]: (field: PaymentFormField): ValidationReport => {
    const { value, type } = field;

    if (value === null && type !== InputTypes.Hidden) {
      return {
        status: field.required
          ? ValidationStatuses.UNDEFINED
          : ValidationStatuses.SUCCESS,
        errors: [],
      };
    }

    if (field.required && value === '') {
      return {
        status: ValidationStatuses.FAILED,
        errors: [],
      };
    }

    const { pattern, errorMessage } = field.params || {};

    if (pattern) {
      if (!convertRegexFromString(pattern).test((value as string).toString())) {
        return {
          status: ValidationStatuses.FAILED,
          errors: errorMessage ? [errorMessage] : [],
        };
      }
    }

    const validator = CommonValidatorMap[field.type];

    if (validator) {
      const { isValid, errorMessage } = validator(field);
      return {
        status: isValid
          ? ValidationStatuses.SUCCESS
          : ValidationStatuses.FAILED,
        errors: errorMessage ? [errorMessage] : [],
      };
    }

    return { status: ValidationStatuses.SUCCESS, errors: [] };
  },
};
